import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Search, Plus, Loader2, RefreshCw, Pencil, Copy, Power, 
  PowerOff, Trash2, Eye, Tag, Percent, DollarSign, Calendar,
  Users, ChevronLeft, ChevronRight, Download, Wand2
} from 'lucide-react';

const API_BASE = window.location.origin || '';

// Status badge colors
const statusColors = {
  ACTIVE: 'bg-green-500/20 text-green-400 border-green-500/30',
  SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  EXPIRED: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  DISABLED: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Type badge colors
const typeColors = {
  PERCENT: 'bg-purple-500/20 text-purple-400',
  FIXED: 'bg-orange-500/20 text-orange-400',
};

const AdminCouponsPage = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showRedemptionsDialog, setShowRedemptionsDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'PERCENT',
    value: '',
    currency: 'EUR',
    starts_at: '',
    ends_at: '',
    status: 'ACTIVE',
    stackable: false,
    usage_limit_total: '',
    usage_limit_per_customer: '',
    min_order_amount: '',
    allowed_airports: '',
    allowed_vehicle_types: '',
    excluded_dates: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Batch generate form
  const [batchForm, setBatchForm] = useState({
    prefix: 'GoTurkeyTransfer-',
    length: 8,
    count: 10,
    type: 'PERCENT',
    value: 10,
    currency: 'EUR',
    starts_at: '',
    ends_at: '',
    usage_limit_total: '',
    usage_limit_per_customer: '',
  });
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCoupons = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('type', typeFilter);
      
      const response = await fetch(`${API_BASE}/api/coupons/admin/list?${params}`, {
        headers: getAuthHeader(),
      });
      
      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCoupons(data.coupons || []);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Fetch coupons error:', error);
      toast.error('Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, searchTerm, statusFilter, typeFilter, pagination.limit]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchCoupons();
  }, [navigate, fetchCoupons]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCoupons(1);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      type: 'PERCENT',
      value: '',
      currency: 'EUR',
      starts_at: '',
      ends_at: '',
      status: 'ACTIVE',
      stackable: false,
      usage_limit_total: '',
      usage_limit_per_customer: '',
      min_order_amount: '',
      allowed_airports: '',
      allowed_vehicle_types: '',
      excluded_dates: '',
    });
    setIsEditing(false);
    setSelectedCoupon(null);
  };

  const openEditDialog = (coupon) => {
    setSelectedCoupon(coupon);
    setIsEditing(true);
    setFormData({
      code: coupon.code || '',
      name: coupon.name || '',
      description: coupon.description || '',
      type: coupon.type || 'PERCENT',
      value: coupon.value?.toString() || '',
      currency: coupon.currency || 'EUR',
      starts_at: coupon.starts_at ? coupon.starts_at.slice(0, 16) : '',
      ends_at: coupon.ends_at ? coupon.ends_at.slice(0, 16) : '',
      status: coupon.status || 'ACTIVE',
      stackable: coupon.stackable || false,
      usage_limit_total: coupon.usage_limit_total?.toString() || '',
      usage_limit_per_customer: coupon.usage_limit_per_customer?.toString() || '',
      min_order_amount: coupon.constraints?.min_order_amount?.toString() || '',
      allowed_airports: coupon.constraints?.allowed_airports?.join(', ') || '',
      allowed_vehicle_types: coupon.constraints?.allowed_vehicle_types?.join(', ') || '',
      excluded_dates: coupon.constraints?.excluded_dates?.join(', ') || '',
    });
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const constraints = {};
      if (formData.min_order_amount) {
        constraints.min_order_amount = parseFloat(formData.min_order_amount);
      }
      if (formData.allowed_airports) {
        constraints.allowed_airports = formData.allowed_airports.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      }
      if (formData.allowed_vehicle_types) {
        constraints.allowed_vehicle_types = formData.allowed_vehicle_types.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      }
      if (formData.excluded_dates) {
        constraints.excluded_dates = formData.excluded_dates.split(',').map(s => s.trim()).filter(Boolean);
      }
      
      const payload = {
        code: formData.code.toUpperCase(),
        name: formData.name,
        description: formData.description || null,
        type: formData.type,
        value: parseFloat(formData.value),
        currency: formData.currency,
        starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
        ends_at: formData.ends_at ? new Date(formData.ends_at).toISOString() : null,
        status: formData.status,
        stackable: formData.stackable,
        usage_limit_total: formData.usage_limit_total ? parseInt(formData.usage_limit_total) : null,
        usage_limit_per_customer: formData.usage_limit_per_customer ? parseInt(formData.usage_limit_per_customer) : null,
        constraints,
      };
      
      const url = isEditing 
        ? `${API_BASE}/api/coupons/admin/${selectedCoupon.id}`
        : `${API_BASE}/api/coupons/admin/create`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(isEditing ? 'Coupon updated' : 'Coupon created');
        setShowCreateDialog(false);
        resetForm();
        fetchCoupons(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to save coupon');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to save coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (coupon) => {
    const action = coupon.effective_status === 'DISABLED' ? 'enable' : 'disable';
    
    try {
      const response = await fetch(`${API_BASE}/api/coupons/admin/${coupon.id}/${action}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Coupon ${action}d`);
        fetchCoupons(pagination.page);
      } else {
        toast.error(data.detail || `Failed to ${action} coupon`);
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(`Failed to ${action} coupon`);
    }
  };

  const handleDuplicate = async (coupon) => {
    try {
      const response = await fetch(`${API_BASE}/api/coupons/admin/${coupon.id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Coupon duplicated');
        fetchCoupons(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to duplicate coupon');
      }
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate coupon');
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${coupon.code}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/coupons/admin/${coupon.id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Coupon deleted');
        fetchCoupons(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to delete coupon');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete coupon');
    }
  };

  const handleViewRedemptions = async (coupon) => {
    setSelectedCoupon(coupon);
    setShowRedemptionsDialog(true);
    setRedemptionsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/coupons/admin/${coupon.id}/redemptions?limit=50`, {
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRedemptions(data.redemptions || []);
      }
    } catch (error) {
      console.error('Fetch redemptions error:', error);
      toast.error('Failed to load redemptions');
    } finally {
      setRedemptionsLoading(false);
    }
  };

  const handleBatchGenerate = async (e) => {
    e.preventDefault();
    setBatchGenerating(true);
    
    try {
      const constraints = {};
      
      const payload = {
        prefix: batchForm.prefix,
        length: parseInt(batchForm.length),
        count: parseInt(batchForm.count),
        type: batchForm.type,
        value: parseFloat(batchForm.value),
        currency: batchForm.currency,
        starts_at: batchForm.starts_at ? new Date(batchForm.starts_at).toISOString() : null,
        ends_at: batchForm.ends_at ? new Date(batchForm.ends_at).toISOString() : null,
        usage_limit_total: batchForm.usage_limit_total ? parseInt(batchForm.usage_limit_total) : null,
        usage_limit_per_customer: batchForm.usage_limit_per_customer ? parseInt(batchForm.usage_limit_per_customer) : null,
        constraints,
      };
      
      const response = await fetch(`${API_BASE}/api/coupons/admin/batch-generate`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratedCodes(data.codes || []);
        toast.success(`Generated ${data.count} coupons`);
        fetchCoupons(1);
      } else {
        toast.error(data.detail || 'Failed to generate coupons');
      }
    } catch (error) {
      console.error('Batch generate error:', error);
      toast.error('Failed to generate coupons');
    } finally {
      setBatchGenerating(false);
    }
  };

  const downloadCodes = () => {
    const csv = generatedCodes.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coupons_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="coupons-title">
                Coupons
              </h1>
              <p className="text-muted-foreground">Manage promo codes and discounts</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowBatchDialog(true)}
                data-testid="batch-generate-btn"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Batch Generate
              </Button>
              <Button 
                onClick={() => { resetForm(); setShowCreateDialog(true); }}
                data-testid="create-coupon-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Coupon
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="bg-secondary border-border mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by code or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                    <SelectItem value="EXPIRED">Expired</SelectItem>
                    <SelectItem value="DISABLED">Disabled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="PERCENT">Percent</SelectItem>
                    <SelectItem value="FIXED">Fixed</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline" data-testid="filter-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Coupons Table */}
          <Card className="bg-secondary border-border">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No coupons found</p>
                  <Button 
                    variant="link" 
                    onClick={() => { resetForm(); setShowCreateDialog(true); }}
                    className="mt-2"
                  >
                    Create your first coupon
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-4 font-medium text-muted-foreground">Code</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Name</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Value</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Validity</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Used</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map((coupon) => (
                        <tr 
                          key={coupon.id} 
                          className="border-b border-border hover:bg-muted/30 transition-colors"
                          data-testid={`coupon-row-${coupon.code}`}
                        >
                          <td className="p-4">
                            <code className="px-2 py-1 bg-muted rounded text-sm font-mono text-foreground">
                              {coupon.code}
                            </code>
                          </td>
                          <td className="p-4">
                            <span className="text-foreground">{coupon.name}</span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${typeColors[coupon.type]}`}>
                              {coupon.type === 'PERCENT' ? <Percent className="h-3 w-3 inline mr-1" /> : <DollarSign className="h-3 w-3 inline mr-1" />}
                              {coupon.type}
                            </span>
                          </td>
                          <td className="p-4 text-foreground">
                            {coupon.type === 'PERCENT' ? `${coupon.value}%` : `€${coupon.value}`}
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(coupon.starts_at)} - {formatDate(coupon.ends_at)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span>{coupon.used_count || 0}</span>
                              {coupon.usage_limit_total && (
                                <span className="text-xs">/ {coupon.usage_limit_total}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs border ${statusColors[coupon.effective_status]}`}>
                              {coupon.effective_status}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewRedemptions(coupon)}
                                title="View Redemptions"
                                data-testid={`view-redemptions-${coupon.code}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(coupon)}
                                title="Edit"
                                data-testid={`edit-${coupon.code}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicate(coupon)}
                                title="Duplicate"
                                data-testid={`duplicate-${coupon.code}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleStatus(coupon)}
                                title={coupon.effective_status === 'DISABLED' ? 'Enable' : 'Disable'}
                                data-testid={`toggle-${coupon.code}`}
                              >
                                {coupon.effective_status === 'DISABLED' ? (
                                  <Power className="h-4 w-4 text-green-500" />
                                ) : (
                                  <PowerOff className="h-4 w-4 text-red-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(coupon)}
                                title="Delete"
                                className="text-red-500 hover:text-red-400"
                                data-testid={`delete-${coupon.code}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchCoupons(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchCoupons(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update coupon details' : 'Create a new discount coupon'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SUMMER2026"
                    required
                    disabled={isEditing}
                    className="uppercase"
                    data-testid="coupon-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Summer Sale Discount"
                    required
                    data-testid="coupon-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>
            </div>

            {/* Discount */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Discount</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(v) => setFormData({ ...formData, type: v })}
                  >
                    <SelectTrigger data-testid="coupon-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENT">Percentage (%)</SelectItem>
                      <SelectItem value="FIXED">Fixed Amount (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="value">Value *</Label>
                  <Input
                    id="value"
                    type="number"
                    min="0"
                    max={formData.type === 'PERCENT' ? 100 : undefined}
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.type === 'PERCENT' ? '10' : '20'}
                    required
                    data-testid="coupon-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(v) => setFormData({ ...formData, currency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="TRY">TRY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Validity */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Validity</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="starts_at">Start Date</Label>
                  <Input
                    id="starts_at"
                    type="datetime-local"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ends_at">End Date</Label>
                  <Input
                    id="ends_at"
                    type="datetime-local"
                    value={formData.ends_at}
                    onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="DISABLED">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Limits */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Usage Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="usage_limit_total">Total Uses</Label>
                  <Input
                    id="usage_limit_total"
                    type="number"
                    min="1"
                    value={formData.usage_limit_total}
                    onChange={(e) => setFormData({ ...formData, usage_limit_total: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="usage_limit_per_customer">Per Customer</Label>
                  <Input
                    id="usage_limit_per_customer"
                    type="number"
                    min="1"
                    value={formData.usage_limit_per_customer}
                    onChange={(e) => setFormData({ ...formData, usage_limit_per_customer: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="stackable"
                  checked={formData.stackable}
                  onCheckedChange={(c) => setFormData({ ...formData, stackable: c })}
                />
                <Label htmlFor="stackable" className="text-sm">
                  Allow stacking with other coupons
                </Label>
              </div>
            </div>

            {/* Constraints */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Constraints (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_order_amount">Min Order Amount (€)</Label>
                  <Input
                    id="min_order_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                    placeholder="No minimum"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="allowed_airports">Allowed Airports</Label>
                  <Input
                    id="allowed_airports"
                    value={formData.allowed_airports}
                    onChange={(e) => setFormData({ ...formData, allowed_airports: e.target.value })}
                    placeholder="IST, SAW, AYT (comma separated)"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="allowed_vehicle_types">Allowed Vehicle Types</Label>
                  <Input
                    id="allowed_vehicle_types"
                    value={formData.allowed_vehicle_types}
                    onChange={(e) => setFormData({ ...formData, allowed_vehicle_types: e.target.value })}
                    placeholder="VAN, MINI (comma separated)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="excluded_dates">Excluded Dates</Label>
                  <Input
                    id="excluded_dates"
                    value={formData.excluded_dates}
                    onChange={(e) => setFormData({ ...formData, excluded_dates: e.target.value })}
                    placeholder="2026-12-25, 2026-01-01"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="save-coupon-btn">
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Generate Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Batch Generate Coupons</DialogTitle>
            <DialogDescription>Generate multiple unique coupon codes at once</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleBatchGenerate} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="batch-prefix">Code Prefix</Label>
                <Input
                  id="batch-prefix"
                  value={batchForm.prefix}
                  onChange={(e) => setBatchForm({ ...batchForm, prefix: e.target.value.toUpperCase() })}
                  placeholder="GoTurkeyTransfer-"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="batch-length">Random Length</Label>
                <Input
                  id="batch-length"
                  type="number"
                  min="4"
                  max="16"
                  value={batchForm.length}
                  onChange={(e) => setBatchForm({ ...batchForm, length: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="batch-count">Count</Label>
                <Input
                  id="batch-count"
                  type="number"
                  min="1"
                  max="500"
                  value={batchForm.count}
                  onChange={(e) => setBatchForm({ ...batchForm, count: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={batchForm.type} 
                  onValueChange={(v) => setBatchForm({ ...batchForm, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENT">Percentage (%)</SelectItem>
                    <SelectItem value="FIXED">Fixed Amount (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="batch-value">Value</Label>
                <Input
                  id="batch-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={batchForm.value}
                  onChange={(e) => setBatchForm({ ...batchForm, value: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="datetime-local"
                  value={batchForm.starts_at}
                  onChange={(e) => setBatchForm({ ...batchForm, starts_at: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="datetime-local"
                  value={batchForm.ends_at}
                  onChange={(e) => setBatchForm({ ...batchForm, ends_at: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Uses Limit</Label>
                <Input
                  type="number"
                  min="1"
                  value={batchForm.usage_limit_total}
                  onChange={(e) => setBatchForm({ ...batchForm, usage_limit_total: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Per Customer Limit</Label>
                <Input
                  type="number"
                  min="1"
                  value={batchForm.usage_limit_per_customer}
                  onChange={(e) => setBatchForm({ ...batchForm, usage_limit_per_customer: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            {generatedCodes.length > 0 && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">Generated {generatedCodes.length} codes</p>
                  <Button type="button" variant="outline" size="sm" onClick={downloadCodes}>
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                <div className="max-h-32 overflow-y-auto text-xs font-mono text-muted-foreground">
                  {generatedCodes.slice(0, 10).join(', ')}
                  {generatedCodes.length > 10 && ` ... and ${generatedCodes.length - 10} more`}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBatchDialog(false)}>
                Close
              </Button>
              <Button type="submit" disabled={batchGenerating}>
                {batchGenerating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Redemptions Dialog */}
      <Dialog open={showRedemptionsDialog} onOpenChange={setShowRedemptionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Redemption History - {selectedCoupon?.code}
            </DialogTitle>
            <DialogDescription>
              View all uses of this coupon code
            </DialogDescription>
          </DialogHeader>
          
          {redemptionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : redemptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No redemptions yet</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 font-medium text-muted-foreground">Booking</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Discount</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} className="border-b border-border">
                      <td className="p-3">
                        <code className="text-sm">{r.booking_id}</code>
                      </td>
                      <td className="p-3">
                        €{r.amount_discounted?.toFixed(2)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {formatDate(r.redeemed_at)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {r.ip || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedemptionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCouponsPage;
